import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse, AxiosError } from 'axios';
import type { ApiConfig, ApiError } from '../types';

interface RetryConfig {
  retries: number;
  retryDelay: number;
  retryCondition?: (error: AxiosError) => boolean;
}

class ApiClient {
  private axiosInstance: AxiosInstance;
  private token: string | null = null;
  private retryConfig: RetryConfig = {
    retries: 3,
    retryDelay: 1000,
    retryCondition: (error: AxiosError) => {
      // Retry on network errors or 5xx server errors
      return !error.response || (error.response.status >= 500 && error.response.status < 600);
    }
  };

  constructor(config: ApiConfig) {
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      withCredentials: false, // Set to false to avoid CORS issues with allow_origins=["*"]
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS, PATCH',
        'Access-Control-Allow-Headers': '*',
        ...config.headers,
      },
    });

    this.setupInterceptors();
    this.setupNetworkDetection();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token and request ID
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Add auth token
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // Add request ID for tracking
        config.headers['X-Request-ID'] = this.generateRequestId();

        // Add timestamp for request timing
        (config as any).metadata = { startTime: Date.now() };

        return config;
      },
      (error) => {
        return Promise.reject(this.handleError(error));
      }
    );

    // Response interceptor for error handling and retry logic
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Log successful requests in development
        if (import.meta.env.DEV) {
          const duration = Date.now() - ((response.config as any).metadata?.startTime || 0);
          console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status} (${duration}ms)`);
        }
        return response;
      },
      async (error: AxiosError) => {
        const config = error.config;
        
        // Log failed requests in development
        if (import.meta.env.DEV && config) {
          const duration = Date.now() - ((config as any).metadata?.startTime || 0);
          console.error(`❌ ${config.method?.toUpperCase()} ${config.url} - ${error.response?.status || 'Network Error'} (${duration}ms)`);
        }

        // Handle retry logic
        if (config && this.shouldRetry(error)) {
          const retryCount = (config as any).__retryCount || 0;
          
          if (retryCount < this.retryConfig.retries) {
            (config as any).__retryCount = retryCount + 1;
            
            // Calculate delay with exponential backoff
            const delay = this.retryConfig.retryDelay * Math.pow(2, retryCount);
            
            console.log(`🔄 Retrying request (${retryCount + 1}/${this.retryConfig.retries}) in ${delay}ms...`);
            
            await this.delay(delay);
            return this.axiosInstance(config);
          }
        }

        const apiError = this.handleError(error);
        
        // Handle authentication errors
        if (apiError.status === 401) {
          this.clearAuthToken();
          window.dispatchEvent(new CustomEvent('auth-error', { detail: apiError }));
        }

        // Emit network error events
        if (this.isNetworkError(apiError)) {
          window.dispatchEvent(new CustomEvent('network-error', { detail: apiError }));
        }

        return Promise.reject(apiError);
      }
    );
  }

  private setupNetworkDetection(): void {
    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('🌐 Network connection restored');
      window.dispatchEvent(new CustomEvent('network-status-change', { 
        detail: { isOnline: true } 
      }));
    });

    window.addEventListener('offline', () => {
      console.log('📵 Network connection lost');
      window.dispatchEvent(new CustomEvent('network-status-change', { 
        detail: { isOnline: false } 
      }));
    });
  }

  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  private shouldRetry(error: AxiosError): boolean {
    return this.retryConfig.retryCondition ? this.retryConfig.retryCondition(error) : false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private isNetworkError(error: ApiError): boolean {
    return error.status === 0 || error.message.toLowerCase().includes('network');
  }

  private handleError(error: AxiosError): ApiError {
    const requestId = error.config?.headers?.['X-Request-ID'] as string;
    
    if (error.response) {
      // Server responded with error status
      const data = error.response.data as any;
      let message = error.message;

      // Extract user-friendly error messages
      if (data?.detail) {
        if (typeof data.detail === 'string') {
          message = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Handle FastAPI validation errors
          message = data.detail.map((err: any) => err.msg).join(', ');
        }
      } else if (data?.message) {
        message = data.message;
      } else {
        // Provide user-friendly messages for common HTTP status codes
        switch (error.response.status) {
          case 400:
            message = 'Invalid request. Please check your input and try again.';
            break;
          case 401:
            message = 'Authentication required. Please log in again.';
            break;
          case 403:
            message = 'You do not have permission to perform this action.';
            break;
          case 404:
            message = 'The requested resource was not found.';
            break;
          case 409:
            message = 'This action conflicts with existing data.';
            break;
          case 413:
            message = 'File size exceeds the maximum limit of 10MB. Please upload a smaller file.';
            break;
          case 422:
            message = 'Validation error. Please check your input.';
            break;
          case 429:
            message = 'Too many requests. Please wait a moment and try again.';
            break;
          case 500:
            message = 'Internal server error. Please try again later.';
            break;
          case 502:
            message = 'Service temporarily unavailable. Please try again later.';
            break;
          case 503:
            message = 'Service maintenance in progress. Please try again later.';
            break;
          default:
            message = `Server error (${error.response.status}). Please try again later.`;
        }
      }

      return {
        status: error.response.status,
        message,
        details: {
          ...data,
          requestId,
          timestamp: new Date().toISOString(),
        },
      };
    } else if (error.request) {
      // Network error - no response received
      const isTimeout = error.code === 'ECONNABORTED';
      const message = isTimeout 
        ? 'Request timeout. Please check your connection and try again.'
        : 'Network error. Please check your internet connection.';

      return {
        status: 0,
        message,
        details: {
          type: 'network_error',
          code: error.code,
          requestId,
          timestamp: new Date().toISOString(),
          isTimeout,
        },
      };
    } else {
      // Request setup error
      return {
        status: 0,
        message: error.message || 'An unexpected error occurred while setting up the request.',
        details: {
          type: 'request_setup_error',
          requestId,
          timestamp: new Date().toISOString(),
        },
      };
    }
  }

  setAuthToken(token: string): void {
    this.token = token;
    // Store token in localStorage for persistence
    localStorage.setItem('auth_token', token);
  }

  clearAuthToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  loadTokenFromStorage(): void {
    const token = localStorage.getItem('auth_token');
    if (token) {
      this.token = token;
    }
  }

  async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.request<T>(config);
    return response.data;
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    console.log('addin user ' + url + ' ' + data + ' ' + config);

    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'PATCH', url, data });
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // File upload method
  async uploadFile<T>(url: string, file: File, additionalData?: Record<string, any>): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return this.request<T>({
      method: 'POST',
      url,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}

// Create and export API client instance
const apiConfig: ApiConfig = {
  baseURL: import.meta.env.VITE_API_URL || 'https://ncc.eidc.gov.ly/api/',
  timeout: 10000,
  headers: {},
};

// Debug logging for API configuration
console.log('🔧 API Configuration:', {
  VITE_API_URL: import.meta.env.VITE_API_URL,
  baseURL: apiConfig.baseURL,
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  prod: import.meta.env.PROD
});

export const apiClient = new ApiClient(apiConfig);

// Initialize token from storage on app start
apiClient.loadTokenFromStorage();

export default apiClient;